import { memo, useEffect, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"
import {
	IconArrowRightOutline18 as ArrowRight,
	IconArrowsLeftRightTrailOutline18 as SwitchIcon,
} from "nucleo-ui-outline-18"
import { FixedSizeList } from "react-window"

import { getCurrencySymbol } from "@productlane/lib/src/currency"
import { Button, Dialog, Input, Popover } from "@productlane/ui"
import {
	Command,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import { queries } from "@productlane/zero/queries"

import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useCompanyOptions } from "@/lib/use-entity-options"
import { useZeroWorkspace } from "@/lib/zero"
import { CompanyLogo } from "./company-logo"

interface MergeCompanyDialogProps {
	companyId: string
	isOpen: boolean
	onOpenChange: (value: boolean) => void
	initialWithId?: string | null
}

interface CompanyRow {
	id: string
	name: string
	logoUrl?: string | null
	statusName?: string | null
	statusColor?: string | null
	tierName?: string | null
	revenue?: number | null
	size?: number | null
	domains?: string[]
}

const isEmpty = (value: string | number | null | undefined) =>
	value === null || value === undefined || value === ""

const dash = <span className="text-tertiary">--</span>

const StatusValue = ({ row }: { row?: CompanyRow }) => {
	if (!row || isEmpty(row.statusName)) return dash
	return (
		<span className="flex items-center gap-2">
			<span
				className="size-3 rounded-[3px]"
				style={{ backgroundColor: row.statusColor ?? "#6366f1" }}
			/>
			{row.statusName}
		</span>
	)
}

const formatRevenue = (value: number | null | undefined, symbol: string) =>
	isEmpty(value) ? null : `${symbol}${value.toLocaleString()}`

export const MergeCompanyDialog = memo(
	({
		companyId,
		isOpen,
		onOpenChange,
		initialWithId,
	}: MergeCompanyDialogProps) => {
		const navigate = useNavigate()

		const [confirmText, setConfirmText] = useState("")
		const [pickerOpen, setPickerOpen] = useState(false)
		const [query, setQuery] = useState("")
		const [withId, setWithId] = useState<string | null>(null)
		// merge (source) goes into with (target); switch direction flips them
		const [sourceIsCurrent, setSourceIsCurrent] = useState(true)

		useEffect(() => {
			if (isOpen && initialWithId) setWithId(initialWithId)
		}, [isOpen, initialWithId])

		const { companies } = useCompanyOptions({ query, enabled: isOpen })
		const [current] = useQuery(queries.companyById(companyId), {
			enabled: isOpen,
		})
		const [other] = useQuery(queries.companyById(withId ?? ""), {
			enabled: isOpen && !!withId,
		})

		const sourceCompany = sourceIsCurrent ? current : other
		const targetCompany = sourceIsCurrent ? other : current

		const [sourceRequests] = useQuery(
			queries.companyRequests(sourceCompany?.id ?? ""),
			{ enabled: isOpen },
		)
		const [targetRequests] = useQuery(
			queries.companyRequests(targetCompany?.id ?? ""),
			{ enabled: isOpen },
		)
		const [sourceUpvotes] = useQuery(
			queries.companyUpvotes(sourceCompany?.id ?? ""),
			{ enabled: isOpen },
		)
		const [targetUpvotes] = useQuery(
			queries.companyUpvotes(targetCompany?.id ?? ""),
			{ enabled: isOpen },
		)
		const [sourceDetails] = useQuery(
			queries.companyDetails(sourceCompany?.id ?? ""),
			{ enabled: isOpen },
		)
		const [targetDetails] = useQuery(
			queries.companyDetails(targetCompany?.id ?? ""),
			{ enabled: isOpen },
		)

		const mergeMutation = api.company.merge.useMutation()

		const filtered = useMemo(
			() =>
				companies.filter(
					(c) =>
						c.id !== companyId &&
						c.name.toLowerCase().includes(query.toLowerCase()),
				),
			[companies, companyId, query],
		)

		const result: CompanyRow | undefined = useMemo(() => {
			if (!sourceCompany || !targetCompany) return undefined
			const pick = <T extends string | number | null | undefined>(
				target: T,
				source: T,
			) => (isEmpty(target) ? source : target)
			return {
				id: targetCompany.id,
				name: targetCompany.name,
				logoUrl: pick(targetCompany.logoUrl, sourceCompany.logoUrl),
				statusName: pick(targetCompany.statusName, sourceCompany.statusName),
				statusColor: isEmpty(targetCompany.statusName)
					? sourceCompany.statusColor
					: targetCompany.statusColor,
				tierName: pick(targetCompany.tierName, sourceCompany.tierName),
				revenue: pick(targetCompany.revenue, sourceCompany.revenue),
				size: pick(targetCompany.size, sourceCompany.size),
				domains: Array.from(
					new Set([
						...(targetCompany.domains ?? []),
						...(sourceCompany.domains ?? []),
					]),
				),
			}
		}, [sourceCompany, targetCompany])

		const sourceContacts = sourceDetails?.contacts.length ?? 0
		const targetContacts = targetDetails?.contacts.length ?? 0
		const resultRequests = sourceRequests.length + targetRequests.length
		const resultUpvotes = sourceUpvotes.length + targetUpvotes.length
		const resultContacts = sourceContacts + targetContacts

		const confirmed = confirmText.trim().toLowerCase() === "merge"
		const canSubmit =
			!!sourceCompany && !!targetCompany && !mergeMutation.isLoading

		const close = () => {
			onOpenChange(false)
			setConfirmText("")
			setWithId(null)
			setQuery("")
			setSourceIsCurrent(true)
		}

		const onMerge = async () => {
			if (!sourceCompany || !targetCompany) {
				toast.error({
					title: "Select a company",
					description: "Pick a company to merge with first.",
				})
				return
			}
			if (!confirmed) {
				toast.error({
					title: "Type merge to confirm",
					description: 'Enter "merge" in the field to confirm this action.',
				})
				return
			}
			const sourceId = sourceCompany.id
			const targetId = targetCompany.id
			const targetName = targetCompany.name

			// close before awaiting: the merge deletes the source row, which can unmount this
			// screen mid-await (when we're sitting on the source) and strand the close call.
			close()
			if (targetId !== companyId) {
				void navigate({
					to: "/customers/$id",
					params: { id: targetId },
					replace: true,
				})
			}

			try {
				const res = await mergeMutation.mutateAsync({ sourceId, targetId })
				analytics.track("merge-company", { sourceId, targetId })
				toast.success({
					title: "Companies merged",
					description: res.mergedLinearCustomer
						? `Merged into ${targetName}, including the Linear customer.`
						: `Merged into ${targetName}.`,
				})
			} catch (error) {
				toast.error({
					title: "Failed to merge",
					description:
						error instanceof Error ? error.message : "Something went wrong.",
				})
			}
		}

		const companyPickerContent = (
			<Command shouldFilter={false}>
				<CommandInput
					placeholder="Search companies..."
					autoFocus
					value={query}
					onValueChange={setQuery}
				/>
				<CommandList className="max-h-none">
					<CommandGroup>
						<div style={{ height: Math.min(filtered.length, 8) * 36 }}>
							<FixedSizeList
								height={Math.min(filtered.length, 8) * 36}
								width="100%"
								itemCount={filtered.length}
								itemData={filtered}
								itemKey={(index, data) => data[index]?.id ?? index}
								itemSize={36}
								overscanCount={5}
							>
								{({ index, style }) => {
									const c = filtered[index]
									if (!c) return null
									return (
										<CommandItem
											value={c.id}
											style={style}
											onSelect={() => {
												setWithId(c.id)
												setPickerOpen(false)
												setQuery("")
											}}
										>
											<div className="flex items-center gap-2">
												<CompanyLogo
													brandfetchData={null}
													fallback={c.name}
													linearLogoUrl={c.logoUrl ?? undefined}
													size="xs"
												/>
												<span className="flex-1 truncate">{c.name}</span>
											</div>
										</CommandItem>
									)
								}}
							</FixedSizeList>
						</div>
					</CommandGroup>
				</CommandList>
			</Command>
		)

		const companyChip = (row?: CompanyRow) => (
			<div className="bg-muted/40 flex min-w-0 select-none items-center gap-2 rounded-md border px-3 py-2">
				{row ? (
					<>
						<CompanyLogo
							brandfetchData={null}
							fallback={row.name}
							linearLogoUrl={row.logoUrl ?? undefined}
							size="xs"
						/>
						<span className="truncate text-sm font-medium">{row.name}</span>
					</>
				) : (
					<span className="text-sm text-tertiary">Select a company</span>
				)}
			</div>
		)

		return (
			<Dialog
				isOpen={isOpen}
				onOpenChange={(open) => (open ? onOpenChange(true) : close())}
				size="lg"
				title="Merge companies"
				description="Tickets, contacts, and properties from the first company move into the second. The first company is then deleted."
				footer={
					<div className="flex items-center gap-2">
						<Button variant="outline" onClick={close}>
							Cancel
						</Button>
						<Button disabled={!canSubmit} onClick={onMerge}>
							Merge
						</Button>
					</div>
				}
			>
				<div className="space-y-5">
					<div className="flex items-center gap-3">
						<div className="min-w-0 flex-1">{companyChip(sourceCompany)}</div>
						<ArrowRight className="size-4 shrink-0 text-tertiary" />
						<div className="min-w-0 flex-1">
							{other ? (
								companyChip(targetCompany)
							) : (
								<Popover
									className="w-72 p-0"
									open={pickerOpen}
									onOpenChange={setPickerOpen}
									align="start"
									content={companyPickerContent}
								>
									<button
										type="button"
										className="bg-muted/40 flex w-full min-w-0 select-none items-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm text-tertiary hover:text-secondary"
									>
										Select a company
									</button>
								</Popover>
							)}
						</div>
						<Button
							variant="outline"
							size="sm"
							Icon={SwitchIcon}
							disabled={!other}
							onClick={() => setSourceIsCurrent((v) => !v)}
						>
							Switch
						</Button>
					</div>

					{other && (
						<MergeTable
							source={sourceCompany}
							target={targetCompany}
							result={result}
							counts={{
								requests: [
									sourceRequests.length,
									targetRequests.length,
									resultRequests,
								],
								contacts: [sourceContacts, targetContacts, resultContacts],
								upvotes: [
									sourceUpvotes.length,
									targetUpvotes.length,
									resultUpvotes,
								],
							}}
						/>
					)}

					{other && (
						<div className="space-y-2">
							<div className="text-sm">
								Type <span className="font-semibold">merge</span> to continue
							</div>
							<Input
								name="merge-confirm"
								value={confirmText}
								onChange={(e) => setConfirmText(e.target.value)}
								placeholder="merge"
								autoComplete="off"
								onKeyDown={(e) => {
									if (e.key === "Enter" && canSubmit) {
										e.preventDefault()
										void onMerge()
									}
								}}
							/>
						</div>
					)}
				</div>
			</Dialog>
		)
	},
)

const Row = ({
	label,
	merge,
	withValue,
	result,
}: {
	label: string
	merge: React.ReactNode
	withValue: React.ReactNode
	result: React.ReactNode
}) => (
	<tr className="border-t">
		<td className="py-2.5 pr-4 text-sm text-secondary">{label}</td>
		<td className="py-2.5 pr-4 text-sm">{merge}</td>
		<td className="py-2.5 pr-4 text-sm">{withValue}</td>
		<td className="py-2.5 text-sm">{result}</td>
	</tr>
)

const MergeTable = ({
	source,
	target,
	result,
	counts,
}: {
	source?: CompanyRow
	target?: CompanyRow
	result?: CompanyRow
	// each tuple is [merge, with, result]
	counts: {
		requests: [number, number, number]
		contacts: [number, number, number]
		upvotes: [number, number, number]
	}
}) => {
	const workspace = useZeroWorkspace()
	const currencySymbol = getCurrencySymbol(workspace?.revenueCurrency)
	const nameCell = (row?: CompanyRow) =>
		row ? (
			<span className="flex items-center gap-2">
				<CompanyLogo
					brandfetchData={null}
					fallback={row.name}
					linearLogoUrl={row.logoUrl ?? undefined}
					size="xs"
				/>
				{row.name}
			</span>
		) : (
			dash
		)

	const text = (value?: string | null) => (isEmpty(value) ? dash : value)
	const revenue = (value?: number | null) =>
		formatRevenue(value, currencySymbol) ?? dash
	const sizeCell = (value?: number | null) =>
		isEmpty(value) ? dash : value.toLocaleString()
	const domainsCell = (domains?: string[]) =>
		domains && domains.length > 0 ? domains.join(", ") : dash

	return (
		<table className="w-full border-collapse">
			<thead>
				<tr className="text-left text-sm font-medium text-secondary">
					<th className="pb-2 pr-4" />
					<th className="pb-2 pr-4">Merge</th>
					<th className="pb-2 pr-4">With</th>
					<th className="pb-2">Result</th>
				</tr>
			</thead>
			<tbody>
				<Row
					label="Name"
					merge={nameCell(source)}
					withValue={nameCell(target)}
					result={nameCell(result)}
				/>
				<Row
					label="Status"
					merge={<StatusValue row={source} />}
					withValue={<StatusValue row={target} />}
					result={<StatusValue row={result} />}
				/>
				<Row
					label="Tier"
					merge={text(source?.tierName)}
					withValue={text(target?.tierName)}
					result={text(result?.tierName)}
				/>
				<Row
					label="Requests"
					merge={counts.requests[0]}
					withValue={counts.requests[1]}
					result={counts.requests[2]}
				/>
				<Row
					label="Contacts"
					merge={counts.contacts[0]}
					withValue={counts.contacts[1]}
					result={counts.contacts[2]}
				/>
				<Row
					label="Upvotes"
					merge={counts.upvotes[0]}
					withValue={counts.upvotes[1]}
					result={counts.upvotes[2]}
				/>
				<Row
					label="Monthly revenue"
					merge={revenue(source?.revenue)}
					withValue={revenue(target?.revenue)}
					result={revenue(result?.revenue)}
				/>
				<Row
					label="Size"
					merge={sizeCell(source?.size)}
					withValue={sizeCell(target?.size)}
					result={sizeCell(result?.size)}
				/>
				<Row
					label="Domains"
					merge={domainsCell(source?.domains)}
					withValue={domainsCell(target?.domains)}
					result={domainsCell(result?.domains)}
				/>
			</tbody>
		</table>
	)
}
